/**
 * The console's view of the ONE UI catalogue.
 *
 * There is no English map in this file and there must never be one again. Both locales are
 * imported from `docs/console-mockup/i18n/ui.{en,th}.json` through the `@catalogue` alias
 * (`vite.config.ts`), which is the same pair of files `docs/console-mockup/build.mjs`
 * renders the client mockup from. One catalogue, two consumers, no copy step:
 *
 *   docs/console-mockup/i18n/ui.en.json ──┬── build.mjs  → index.html / index-full / artifact
 *   docs/console-mockup/i18n/ui.th.json ──┴── this file  → the React console
 *
 * What keeps them honest, in both directions:
 *   • `i18n.catalogue.test.ts` scans this package's `src` for key literals and fails if any
 *     of them is missing from either locale, and fails if the two locales' key sets differ.
 *   • `build.mjs` gate 2 (dead keys) scans this package's `src` too, so a key only the
 *     console uses is *live*, and a key nobody uses at all is still caught.
 *   • `t()` records every miss in `missedKeys`; `src/test/setup.ts` fails the test that
 *     caused one, which covers the keys that are assembled at run time (`block.${op}.label`).
 *
 * PLAN-v2 §6 fixes the key shape: `namespace.subject.role`, where the subject is a stable
 * technical identifier (`amount_thb`, `RB-01`, `gte`, `STEP_UP`, `rb`, `ITMX`) and never
 * display text, so rewording any language cannot break a key. `{slot}` placeholders make
 * word order data rather than string concatenation.
 */
import { useSyncExternalStore } from 'react';
import EN from '@catalogue/ui.en.json';
import TH from '@catalogue/ui.th.json';

export type Locale = 'en' | 'th';

export const LOCALES: readonly Locale[] = ['en', 'th'];
export const DEFAULT_LOCALE: Locale = 'en';

/** Where the chosen language is remembered. One key, one tab-independent value. */
export const LOCALE_STORAGE_KEY = 'kkp.rule-admin.locale';

const CATALOGUES: Record<Locale, Record<string, string>> = { en: EN, th: TH };

/**
 * Keys `t()` could not find in any catalogue. Exported so the test setup can fail the test
 * that produced one — the only practical detector for keys built at run time.
 */
export const missedKeys = new Set<string>();

const isLocale = (value: string | null): value is Locale => value === 'en' || value === 'th';

/**
 * Read at module init, not in an effect: `defs.ts` builds Blockly labels during the first
 * render, and a locale that arrives one paint later would leave English blocks on a Thai page.
 */
function storedLocale(): Locale {
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(raw)) return raw;
  } catch {
    /* private mode, or no window at all — English is a fine answer */
  }
  return DEFAULT_LOCALE;
}

let currentLocale: Locale = storedLocale();

/** Components that re-render when the language changes. */
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getLocale(): Locale {
  return currentLocale;
}

/** Re-renders on every language change. `t()` is read during render, so this is the seam. */
export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, () => DEFAULT_LOCALE);
}

/** Sets the active locale, remembers it, keeps `<html lang>` in step, and repaints. */
export function setLocale(locale: Locale): void {
  if (locale === currentLocale) return;
  currentLocale = locale;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* the switch still works for this page load */
  }
  applyDocumentLang(locale);
  for (const listener of [...listeners]) listener();
}

export function applyDocumentLang(locale: Locale = currentLocale): void {
  try {
    document.documentElement.lang = locale;
  } catch {
    /* no document — a unit test importing this module in node */
  }
}

/**
 * `t('nav.dash.label')` → 'Dashboard'. `{slot}` placeholders are filled from `vars`, so
 * word order is data and a translator can move them.
 *
 * A key missing from the active locale falls back to English rather than to a blank; a key
 * missing from both returns itself, which is loud in the UI and harmless at run time. Both
 * cases are recorded in `missedKeys` so a test, not a demo, is where they surface.
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  const template = CATALOGUES[currentLocale][key] ?? EN[key];
  if (template === undefined) missedKeys.add(key);
  const text = template ?? key;
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    return value === undefined ? whole : String(value);
  });
}

/**
 * Is `key` in the catalogue (English, which every key must have)? For text that has its own
 * fallback — an engine problem whose `code` this build does not know yet is shown in the
 * engine's English words — so asking does not record a miss the way `t()` does.
 */
export function hasKey(key: string): boolean {
  return CATALOGUES.en[key] !== undefined;
}

/** Exposed for the catalogue tests and for anything that needs to enumerate the catalogue. */
export function catalogueKeys(locale: Locale = DEFAULT_LOCALE): string[] {
  return Object.keys(CATALOGUES[locale]);
}

/**
 * Test-only reset. `setLocale(DEFAULT_LOCALE)` cannot do this job: it returns early when the
 * locale already matches, which leaves `<html lang="th">` set by the previous test file.
 */
export function resetLocaleForTests(): void {
  currentLocale = DEFAULT_LOCALE;
  missedKeys.clear();
  applyDocumentLang(DEFAULT_LOCALE);
}
